import React from "react";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  UserCheck,
  UserPlus,
  Calendar,
  CalendarCheck,
  Lock,
  XCircle,
} from "lucide-react";
import { WaitlistDetails } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchWaitlistDetails/types";
import { formatSessionDateTime } from "@/modules/eventRegistration/shared/utils/dateFormatting";

interface SessionInformationCardProps {
  waitlistDetails: WaitlistDetails | null;
  waitlistLength: number;
  availableSpots: number;
}

export default function SessionInformationCard({
  waitlistDetails,
  waitlistLength,
  availableSpots,
}: SessionInformationCardProps) {
  if (!waitlistDetails) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6">
        <p className="text-gray-700 dark:text-gray-300">
          Loading session info...
        </p>
      </div>
    );
  }

  const {
    name,
    startDate,
    endDate,
    timezoneIdentifier,
    timezoneShortName,
    enrolledCount,
    maxEnrollments,
    isSessionFinalized,
  } = waitlistDetails;
  const isFull = waitlistDetails.availableSeatCount <= 0;

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h2 className="text-xl font-normal text-gray-900 dark:text-white mb-3">
            {name || "Untitled Event"}
          </h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Calendar className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium">Start:</span>
              <span>
                {formatSessionDateTime(
                  startDate,
                  timezoneIdentifier,
                  timezoneShortName,
                )}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <CalendarCheck className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium">End:</span>
              <span>
                {formatSessionDateTime(
                  endDate,
                  timezoneIdentifier,
                  timezoneShortName,
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {waitlistDetails.isSessionCancelled ? (
            <Badge
              className="px-3 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
              variant="secondary"
            >
              <XCircle className="h-3 w-3 mr-1" />
              Session Cancelled
            </Badge>
          ) : isSessionFinalized ? (
            <Badge
              className="px-3 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
              variant="secondary"
            >
              <Lock className="h-3 w-3 mr-1" />
              Session Finalized
            </Badge>
          ) : isFull ? (
            <Badge
              className="px-3 py-1 bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300"
              variant="secondary"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              Session Full
            </Badge>
          ) : (
            <Badge
              className="px-3 py-1 bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300"
              variant="secondary"
            >
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Open for Registration
            </Badge>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Registered
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {enrolledCount}/{maxEnrollments}
              </p>
            </div>
            <div className="p-2 bg-gray-100 dark:bg-slate-800 rounded-lg">
              <UserCheck className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Current enrollment status
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Available Spots
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {availableSpots}
              </p>
            </div>
            <div className="p-2 bg-gray-100 dark:bg-slate-800 rounded-lg">
              <UserPlus className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {availableSpots > 0
              ? "Ready for registration"
              : "Session at capacity"}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                On Waitlist
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {waitlistLength}
              </p>
            </div>
            <div className="p-2 bg-gray-100 dark:bg-slate-800 rounded-lg">
              <Clock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Users waiting for spots
          </p>
        </div>
      </div>
    </div>
  );
}
